import { Ledger } from "@common/contracts/ledger.contract";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { View } from "@common/types/views/views";

const LEDGER_ACCOUNT_COLUMN_PREFIX = "ledgerAccount";
const LEDGER_PAYABLE_COLUMN_PREFIX = "ledgerPayable";
const LEDGER_RECEIVABLE_COLUMN_PREFIX = "ledgerReceivable";
const SUGGESTED_LEDGER_COLUMN_PREFIX = "suggestedLedger";

export function buildLedgerAccountColumnIdByName(ledger: Ledger): string {
    return buildNamedLedgerColumnId(LEDGER_ACCOUNT_COLUMN_PREFIX, ledger);
}

export function buildLedgerPayableColumnIdByName(ledger: Ledger): string {
    return buildNamedLedgerColumnId(LEDGER_PAYABLE_COLUMN_PREFIX, ledger);
}

export function buildLedgerReceivableColumnIdByName(ledger: Ledger): string {
    return buildNamedLedgerColumnId(LEDGER_RECEIVABLE_COLUMN_PREFIX, ledger);
}

export function buildSuggestedLedgerColumnIdByName(ledger: Ledger): string {
    return buildNamedLedgerColumnId(SUGGESTED_LEDGER_COLUMN_PREFIX, ledger);
}

export function getLedgerIdFromColumnId(columnId: string): string | undefined {
    const [, ledgerId, encodedName] = columnId.split(":");
    if (encodedName) {
        return undefined;
    }
    return ledgerId || undefined;
}

export function getLedgerNameFromColumnId(
    columnId: string,
): string | undefined {
    const [, , encodedName] = columnId.split(":");
    if (!encodedName) {
        return undefined;
    }
    try {
        return decodeURIComponent(encodedName);
    } catch {
        return undefined;
    }
}

export function getLedgerBasisFromColumnId(
    columnId: string,
): AccountingLedgerBasis | undefined {
    const [, basis, encodedName] = columnId.split(":");
    if (!encodedName) {
        return undefined;
    }
    if (
        basis === AccountingLedgerBasis.cash ||
        basis === AccountingLedgerBasis.accruals
    ) {
        return basis;
    }
    return undefined;
}

function getLedgerViewColumnPrefix(columnId: string): string | undefined {
    return [
        LEDGER_ACCOUNT_COLUMN_PREFIX,
        LEDGER_PAYABLE_COLUMN_PREFIX,
        LEDGER_RECEIVABLE_COLUMN_PREFIX,
        SUGGESTED_LEDGER_COLUMN_PREFIX,
    ].find((prefix) => columnId.startsWith(`${prefix}:`));
}

export function isLedgerReceivableColumnId(columnId: string): boolean {
    return (
        getLedgerViewColumnPrefix(columnId) === LEDGER_RECEIVABLE_COLUMN_PREFIX
    );
}

export function isLedgerPayableColumnId(columnId: string): boolean {
    return getLedgerViewColumnPrefix(columnId) === LEDGER_PAYABLE_COLUMN_PREFIX;
}

export function flattenLedgersByEntity(
    ledgersByEntityId: Record<number, Ledger[]> | undefined,
): Ledger[] {
    return Object.values(ledgersByEntityId ?? {})
        .flat()
        .sort((first, second) => {
            if (first.entityId !== second.entityId) {
                return first.entityId - second.entityId;
            }
            if (first.isPrimary !== second.isPrimary) {
                return first.isPrimary ? -1 : 1;
            }
            return first.name.localeCompare(second.name);
        });
}

export function uniqueLedgersByBasisAndName(ledgers: Ledger[]): Ledger[] {
    const ledgersByKey = new Map<string, Ledger>();
    for (const ledger of ledgers) {
        const key = buildLedgerNameKey(ledger);
        if (!ledgersByKey.has(key)) {
            ledgersByKey.set(key, ledger);
        }
    }
    return [...ledgersByKey.values()];
}

export function injectLedgerColumns<TView extends View>({
    view,
    ledgers,
    replaceColumnIds,
    buildColumnIds,
}: {
    view: TView;
    ledgers: Ledger[];
    replaceColumnIds: string[];
    buildColumnIds: (ledger: Ledger) => string[];
}): TView {
    if (ledgers.length === 0) {
        return view;
    }

    const [anchorColumnId, ...removedColumnIds] = replaceColumnIds;
    const replacementColumnIds = ledgers.flatMap(buildColumnIds);
    const replacementColumnIdsSet = new Set(replacementColumnIds);
    const managedColumnPrefixes = new Set(
        replacementColumnIds
            .map(getLedgerViewColumnPrefix)
            .filter((prefix) => prefix !== undefined),
    );
    const replacementColumns = replacementColumnIds.map((id) => ({ id }));
    const seenColumnIds = new Set<string>();
    let hasInsertedReplacementColumns = false;
    const insertReplacementColumns = () =>
        replacementColumns.filter((replacementColumn) => {
            if (seenColumnIds.has(replacementColumn.id)) {
                return false;
            }
            seenColumnIds.add(replacementColumn.id);
            return true;
        });

    const columns = view.columns.flatMap((column) => {
        if (column.id === anchorColumnId) {
            hasInsertedReplacementColumns = true;
            return insertReplacementColumns();
        }

        if (removedColumnIds.includes(column.id)) {
            if (!hasInsertedReplacementColumns) {
                hasInsertedReplacementColumns = true;
                return insertReplacementColumns();
            }
            return [];
        }

        const ledgerColumnPrefix = getLedgerViewColumnPrefix(column.id);
        if (
            ledgerColumnPrefix &&
            managedColumnPrefixes.has(ledgerColumnPrefix) &&
            !replacementColumnIdsSet.has(column.id)
        ) {
            return [];
        }

        if (replacementColumnIds.includes(column.id)) {
            if (seenColumnIds.has(column.id)) {
                return [];
            }
            seenColumnIds.add(column.id);
            return [column];
        }

        seenColumnIds.add(column.id);
        return [column];
    });
    return {
        ...view,
        columns,
    };
}

function buildNamedLedgerColumnId(prefix: string, ledger: Ledger): string {
    return `${prefix}:${ledger.basis}:${encodeURIComponent(ledger.name)}`;
}

function buildLedgerNameKey(ledger: Ledger): string {
    return `${ledger.basis}:${ledger.name}`;
}
